import React from 'react';
import {
  PAGE_KINDS,
  PREVIEW_SANDBOX,
  PREVIEW_SCHEMES,
  PREVIEW_WIDTHS,
  PageKind,
  PreviewScheme,
  previewScale,
} from './siteSpec';

/**
 * The live preview of the generated site.
 *
 * The frame's only sandbox grant is `allow-scripts` — no same-origin, no
 * navigation, no popups, so nothing in there can reach this page except by
 * posting a message. It still loads the Google Fonts stylesheet, which is the
 * same `<link>` the built site emits, so the type is real.
 *
 * The HTML comes from a server-side compile of the SAME shell that produces the
 * Hugo layouts (see `jcseo/sitegen/slots.py`), so this is not an approximation
 * of the built site — it is the built site's markup with sample content in it.
 */

export function SiteScaffoldPreview({
  html,
  kind,
  onKindChange,
  loading,
  error,
  usingRealPages,
  onOpenInTab,
  onBuildForReal,
  building,
  scheme,
  onSchemeChange,
  linksTo,
  path,
  pageTitle,
  onClearPath,
}: {
  html: string;
  kind: PageKind;
  onKindChange: (kind: PageKind) => void;
  loading: boolean;
  error?: string | null;
  usingRealPages: boolean;
  onOpenInTab: () => void;
  /** Run a real `hugo` build and show its output. Absent = not offered. */
  onBuildForReal?: () => void;
  building?: boolean;
  scheme: PreviewScheme;
  onSchemeChange: (scheme: PreviewScheme) => void;
  /** Where the preview's links point — the site's public address, if it has one. */
  linksTo?: string | null;
  /** The page reached by following a link inside the preview. */
  path?: string;
  pageTitle?: string;
  onClearPath: () => void;
}) {
  const [width, setWidth] = React.useState('desktop');
  const frameWidth = PREVIEW_WIDTHS.find((w) => w.value === width)?.px || 1440;

  // The pane is half the studio, so a 1440px page has to be scaled to fit it.
  // Measured rather than assumed: the split is resizable and the window is not
  // a fixed size.
  // Bumped to force the frame to re-mount. `srcDoc` is only re-applied when the
  // string CHANGES, so a frame that has navigated somewhere else — which it
  // could, before the preview carried a `<base target=_blank>` — has no way
  // back on its own, and re-rendering with identical HTML does nothing.
  const [frameNonce, setFrameNonce] = React.useState(0);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const [available, setAvailable] = React.useState(0);
  React.useEffect(() => {
    const node = stageRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setAvailable(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const scale = previewScale(frameWidth, available);

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {PAGE_KINDS.map((k) => (
            <button
              key={k.value}
              onClick={() => onKindChange(k.value)}
              className={`px-3 py-1 text-xs ${
                kind === k.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        {/* The site follows the VIEWER's system setting and has no toggle of
            its own. This one is the studio's: without it, editing the light
            palette on a dark machine showed you the dark one. */}
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden ml-auto">
          {PREVIEW_SCHEMES.map((s) => (
            <button
              key={s.value}
              onClick={() => onSchemeChange(s.value)}
              title={s.hint}
              className={`px-2.5 py-1 text-xs ${
                scheme === s.value
                  ? 'bg-gray-800 dark:bg-gray-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {PREVIEW_WIDTHS.map((w) => (
            <button
              key={w.value}
              onClick={() => setWidth(w.value)}
              className={`px-3 py-1 text-xs ${
                width === w.value
                  ? 'bg-gray-800 dark:bg-gray-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>

        <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
          {frameWidth}px{scale < 1 ? ` · ${Math.round(scale * 100)}%` : ''}
        </span>
        {/* The embedded frame is scaled down to fit half a screen, which is
            right for working but wrong for judging. This opens the same page at
            the full width of a real window. */}
        <button
          type="button"
          onClick={() => setFrameNonce((n) => n + 1)}
          title="Reload the preview frame"
          className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={onOpenInTab}
          title="Open this page in a new browser tab, at full size"
          className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
        >
          Open in a tab ↗
        </button>
        {/* Everything else here is Python rendering the same shell the Hugo
            layouts come from, which settles structure but not what Goldmark
            does with a body. This runs the real thing. */}
        {onBuildForReal && (
          <button
            type="button"
            onClick={onBuildForReal}
            disabled={building}
            title="Run a real Hugo build of this design and show what it produced"
            className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 whitespace-nowrap"
          >
            {building ? 'Building…' : 'Build it for real'}
          </button>
        )}
        {loading && (
          <span className="text-xs text-gray-400 dark:text-gray-500">Rendering…</span>
        )}
      </div>

      {/* Say whose content this is, and where a click goes.
          A preview full of invented section names reads as a mock; one built
          from the site's own pages is evidence. And a link that does nothing
          when clicked reads as broken unless the reason is on screen — which is
          exactly how it read before this line existed. */}
      <div className="px-4 py-1 text-[11px] text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
        {usingRealPages
          ? 'Showing this site’s own pages.'
          : 'This site has no pages yet - showing example content in your design.'}{' '}
        {path ? (
          <>
            Reading <strong>{pageTitle || path}</strong>{' '}
            <span className="font-mono">{path}</span>
            <button
              type="button"
              onClick={onClearPath}
              className="ml-2 underline hover:text-gray-600 dark:hover:text-gray-300"
            >
              back
            </button>
          </>
        ) : (
          <>
            Click a link to read that page. Links to other sites open in a new tab
            {linksTo ? (
              <>
                , and the rest resolve against{' '}
                <span className="font-mono">{linksTo}</span>
              </>
            ) : null}
            .
          </>
        )}
      </div>

      {error ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-red-600 dark:text-red-400 max-w-md text-center">{error}</p>
        </div>
      ) : (
        <div
          ref={stageRef}
          className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-3"
        >
          {/* The frame is rendered at its REAL width and scaled down, rather
              than squeezed into the pane. Squeezing it made the site take its
              tablet layout at every setting, so you were reviewing a design
              nobody would see on a laptop. */}
          {/* Both heights are unconditional. Branching on "have we measured the
              pane yet" left the wrapper auto-height on the first paint, and a
              percentage height inside an auto-height parent resolves to zero —
              so the frame collapsed for a frame before snapping open. At the
              starting scale of 1 these are just 100%. */}
          <div className="mx-auto h-full" style={{ width: frameWidth * scale }}>
            {/* `allow-scripts` WITHOUT `allow-same-origin` — the combination
                is the point, and it is the whole grant. See
                `PREVIEW_SANDBOX`. */}
            <iframe
              key={frameNonce}
              title="site preview"
              sandbox={PREVIEW_SANDBOX}
              srcDoc={html}
              className="border-0 bg-white"
              style={{
                width: `${frameWidth}px`,
                height: `${100 / scale}%`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                boxShadow: '0 0 0 1px rgba(0,0,0,.08)',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SiteScaffoldPreview;
